//@ts-nocheck
import { memo, useEffect, useState, useRef } from "react";
import React from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Box,
  Chip,
  TextField,
} from '@mui/material';
import { Close, ExpandMore } from '@mui/icons-material';
import '../../components/commonComponent/MultiSelectWithButton/multiselect.scss';


interface Option {
  id: string;
  code: string;
  label: string;
}


interface MultiSelectSpecificSitesProps {
  options: Option[];
  label?: string;
  selectedValues: string[];
  onApply: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  showRequired?: boolean;
  isAllSelect?: boolean;
  noCafeCodeShow?: boolean;
  isexmenureq?: any,
  setSelectedCafeOptions?: any,
  selectAllLabel?: string;
  notifyOnClear?: boolean;
}


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MAX_MENU_HEIGHT = ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP;
const MENU_WIDTH = 250;


const MultiSelectSpecificSites: React.FC<MultiSelectSpecificSitesProps> = ({
  options,
  label,
  selectedValues,
  onApply,
  disabled,
  placeholder,
  showRequired = true,
  isAllSelect,
  noCafeCodeShow,
  isexmenureq,
  setSelectedCafeOptions,
  selectAllLabel,
  notifyOnClear = false
}) => {
  const [tempSelectedOptions, setTempSelectedOptions] = useState<string[]>(selectedValues);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>(''); // For search input
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options); // Initially set filteredOptions to options
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Whenever options prop changes, reset filteredOptions to options
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    setTempSelectedOptions(selectedValues);
  }, [selectedValues]);


  // Handles the search input and filters options accordingly
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (query.trim() === '') {
      setFilteredOptions(options);
    } else {
      const lowercasedQuery = query.toLowerCase();
      setFilteredOptions(
        options.filter((option) => {
          const optionId = String(option.id).toLowerCase();
          const optionLabel = option.label.toLowerCase();
          return optionLabel.includes(lowercasedQuery) || optionId.includes(lowercasedQuery);
        })
      );
    }
  };

  // Handles changes when selecting options from the dropdown
  const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target;
    const selectedValues = typeof value === 'string' ? value.split(',') : value as string[];
    setTempSelectedOptions((prev) => {
      const validSelections = selectedValues.filter((selected) => options.some(option => option.code === selected));
      return [...new Set([...prev, ...validSelections])];
    });
  };

  // Handles checkbox when selecting options 
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, optionCode: string) => {
    setTempSelectedOptions((prev) => {
      if (event.target.checked) {
        return [...new Set([...prev, optionCode])];
      } else {
        return prev.filter((code) => code !== optionCode);
      }
    });
  };

  // Handle Select All functionality
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all available options
      const allCodes = options.map(option => option.code);
      setTempSelectedOptions(allCodes);
    } else {
      // Deselect all
      setTempSelectedOptions([]);
    }
  };

  // Check if all options are selected
  const isAllSelected = options.length > 0 && tempSelectedOptions.length === options.length;

  // Clear selections and reset filters
  const handleClear = () => {
    setTempSelectedOptions([]);
    setSearchQuery(''); // Clear the search input as well
    setFilteredOptions(options); // Reset filtered options to the full options
    if (notifyOnClear) {
      onApply([]);
    }
  };


  // Apply selected options and close the dropdown
  const handleApply = () => {
    onApply(tempSelectedOptions);
    setOpen(false);
  };


  // Delete a selected tag (chip) from the selected options
  const handleDeleteTag = (tagToDelete: string) => {
    setTempSelectedOptions((prev) => {
      const updatedSelection = prev.filter((tag) => tag !== tagToDelete);
      onApply(updatedSelection);
      return updatedSelection;
    });
  };


  // Handle dropdown close
  const handleClose = () => {
    setOpen(false);
  };

  // Handle dropdown open
  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };


  return (
    <FormControl fullWidth className='multi-select-wrapper multi-select-search'>
  {label && <label id="multi-select-label" className={`${disabled ? 'disabled' : ''}`}>{label} {showRequired ? <span>*</span> : null}</label>}
      <Select
        labelId="multi-select-label"
        multiple
        value={disabled ? selectedValues : tempSelectedOptions}
        IconComponent={ExpandMore}
        onChange={handleSelectChange}
        onClose={handleClose}
        open={open}
        onOpen={handleOpen}
        disabled={disabled}
        placeholder={placeholder}
        renderValue={(selected) => (
          <Box>
            {selected.map((value) => {
              const option = options.find(opt => opt.code === value);
              return (
                <Chip
                  key={value}
                  label={
                    option
                      ? isAllSelect || noCafeCodeShow
                        ? option.label
                        : `${option.label} (${option.code})`
                      : value
                  }

                  onDelete={() => handleDeleteTag(value)}
                  deleteIcon={<Close onMouseDown={(event) => event.stopPropagation()} />}
                />
              );
            })}
          </Box>
        )}
        className='multi-select'
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: MAX_MENU_HEIGHT,
              width: MENU_WIDTH,
            },
            className: 'multi-select-menu multi-select-search-menu',
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          disableScrollLock: true,
          disablePortal: true,
        }}
      >
        {/* Search bar */}
        <Box p={1} className='search-wrapper'>
          <TextField
            inputRef={inputRef}
            fullWidth
            value={searchQuery} // This ensures the input box reflects what you type
            onChange={handleSearchChange}
            placeholder={`Type ${placeholder}`}
            variant="outlined"
            size="medium"
            disabled={disabled}
            autoComplete="off"
          />
        </Box>

        <Box className="list-wrap">
          {/* Select All option */}
          {selectAllLabel && searchQuery.trim() === '' && (
            <MenuItem key="select-all" value="select-all" disableRipple>
              <ListItemText primary={selectAllLabel} />
              <Checkbox
                checked={isAllSelected}
                onChange={handleSelectAll}
              />
            </MenuItem>
          )}
          
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <MenuItem key={option.id} value={option.code} disableRipple>
                <ListItemText
                  primary={
                    isAllSelect || noCafeCodeShow
                      ? option.label
                      : `${option.label} (${option.code})`
                  }
                />
                <Checkbox
                  checked={tempSelectedOptions.includes(option.code)}
                  onChange={(event) => handleCheckboxChange(event, option.code)} // Handle checkbox change
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No results found</MenuItem>
          )}
        </Box>


        <Box className="flex justify-content-between align-items-center list-footer">
          <Button
            onClick={handleClear}
            className='cancel-btn'
            disableRipple
            disabled={tempSelectedOptions.length === 0} // Disable cancel if no selection made
          >
            Cancel
          </Button>
          <Button onClick={handleApply} className='primary-medium-green-btn' disableRipple>
            Done
          </Button>
        </Box>
      </Select>
    </FormControl>
  );
};


export default memo(MultiSelectSpecificSites);